import {
	IsArray,
	IsIn,
	IsNumber,
	IsOptional,
	IsString,
	MaxLength,
} from 'class-validator';
import type { TabConfigurationVisibility } from '../entity/list-tab-configuration.entity';
import type { RoleReference } from './view-layout.dto';

// Every configurable list in the app (i.e. every listKey rendered with a
// Configure List button) is registered here so its default tabs can be managed
// from Settings > Tab Configurations. Keep this in sync with the listKeys used
// by ListView across the modules. `event` is retained for backward compatibility
// even though no list currently uses it.
export const TAB_CONFIGURATION_LIST_KEYS = [
	'access_logs',
	'automation_batches',
	'automation_flows',
	'automation_reports',
	'automation_target_list',
	'automation_target_lists',
	'automation_trigger',
	'automation_triggers',
	'automation-reports-batch-embedded',
	'automation-reports-trigger-embedded',
	'automation-target-lists-embedded',
	'call_history',
	'call-history-client-embedded',
	'call-history-vendor-embedded',
	'client',
	'client_ecommerce_user',
	'client_licenses',
	'client_list',
	'client_list_clients',
	'client_location',
	'client_shop',
	'client_trip_history',
	'client_visits',
	'client-children-embedded',
	'client-ownership-embedded',
	'client-contacts-embedded',
	'client-files-embedded',
	'clientCategoryInsights',
	'clientItemHistory',
	'competitive-price-client-embedded',
	'competitive-price-item-embedded',
	'conversation_agent',
	'conversation_analyzer',
	'conversation_session',
	'conversation_tool',
	'conversation_voice_profile',
	'conversation-agent-embedded',
	'driver',
	'ecommerce_user',
	'ecommerce_user_clients',
	'email_templates',
	'event',
	'group',
	'group-users',
	'invoice',
	'invoice_detail',
	'invoice_shop',
	'invoice-client-embedded',
	'invoice-order-embedded',
	'issue',
	'issue-client-embedded',
	'issue-files-embedded',
	'issue-link-embedded',
	'issue-merchandiser-embedded',
	'issue-sourcing-request-embedded',
	'issue-vehicle-embedded',
	'issue-vendor-embedded',
	'item',
	'item-files-embedded',
	'item-purchased-together-embedded',
	'item-warehouse-embedded',
	'itemClientHistory',
	'itemLot-item-embedded',
	'itemLot-location-embedded',
	'itemLot-warehouse-embedded',
	'itemWarehouse-item-embedded',
	'lead_eligibility',
	'license',
	'license_match',
	'license-audit-embedded',
	'license-disciplinary-embedded',
	'license-discrepancies-embedded',
	'license-files-embedded',
	'license-links-embedded',
	'location',
	'locationItem',
	'login_history',
	'merchandiser',
	'opportunity',
	'opportunity-client-embedded',
	'opportunity-files-embedded',
	'order',
	'order_detail',
	'order-client-embedded',
	'order-invoice-embedded',
	'ownership',
	'payment_methods',
	'payment_schedule_occurrences',
	'payment_schedules',
	'price_contract',
	'price_contract_client',
	'price_contract_item',
	'price-contract-client-embedded',
	'price-contract-item-embedded',
	'promo',
	'purchase_order',
	'purchase_order_detail',
	'purchase_order_receipt',
	'purchase_order_receipts_main',
	'purchase_order_receipts_vendor_embedded',
	'purchase-order-vendor-embedded',
	'quote',
	'quote_shop',
	'quote-client-embedded',
	'registry',
	'route',
	'sensor_device',
	'sensorReading-sensor-embedded',
	'sourcing_request',
	'sourcing-request-client-embedded',
	'sourcing-request-files-embedded',
	'sourcing-request-item-embedded',
	'sourcing-request-vendor-embedded',
	'statement',
	'statement-client-embedded',
	'system_sent_emails',
	'task',
	'task-client-embedded',
	'task-issue-embedded',
	'task-license-embedded',
	'task-opportunity-embedded',
	'task-sourcing-request-embedded',
	'team',
	'transaction_summary',
	'transaction-client-embedded',
	'transaction-invoice-embedded',
	'transaction-order-embedded',
	'trip',
	'vehicle',
	'vendor',
	'vendor_item',
	'warehouse',
] as const;

export type TabConfigurationListKey =
	(typeof TAB_CONFIGURATION_LIST_KEYS)[number];

export class CreateTabConfigurationDto {
	@IsString()
	@MaxLength(255)
	public name!: string;

	@IsString()
	@IsIn(TAB_CONFIGURATION_LIST_KEYS)
	public listKey!: TabConfigurationListKey;

	@IsString()
	@IsIn(['organization', 'role'])
	public visibility!: TabConfigurationVisibility;

	@IsArray()
	@IsNumber({}, { each: true })
	@IsOptional()
	public roleIds?: Array<number>;

	@IsArray()
	@IsString({ each: true })
	public tabIds!: Array<string>;
}

export class UpdateTabConfigurationDto {
	@IsString()
	@MaxLength(255)
	@IsOptional()
	public name?: string;

	@IsString()
	@IsIn(['organization', 'role'])
	@IsOptional()
	public visibility?: TabConfigurationVisibility;

	@IsArray()
	@IsNumber({}, { each: true })
	@IsOptional()
	public roleIds?: Array<number>;

	@IsArray()
	@IsString({ each: true })
	@IsOptional()
	public tabIds?: Array<string>;
}

export interface TabConfigurationDefinitionDto {
	id: number;
	name: string;
	listKey: string;
	visibility: TabConfigurationVisibility;
	sharedRoles: Array<RoleReference>;
	sortOrder: number;
	tabIds: Array<string>;
	ownerUserId: number;
	createdDate: Date;
	modifiedDate: Date | null;
}

export type GetTabConfigurationsResponseDto =
	| {
			success: true;
			data: Array<TabConfigurationDefinitionDto>;
	  }
	| {
			success: false;
			error: string;
	  };

export type GetTabConfigurationResponseDto =
	| {
			success: true;
			data: TabConfigurationDefinitionDto;
	  }
	| {
			success: false;
			error: string;
	  };

export type CreateTabConfigurationResponseDto =
	| {
			success: true;
			data: TabConfigurationDefinitionDto;
	  }
	| {
			success: false;
			error: string;
	  };

export type UpdateTabConfigurationResponseDto =
	| {
			success: true;
			data: TabConfigurationDefinitionDto;
	  }
	| {
			success: false;
			error: string;
	  };

export type DeleteTabConfigurationResponseDto =
	| {
			success: true;
			message: string;
	  }
	| {
			success: false;
			error: string;
	  };

export type ResolveTabConfigurationResponseDto =
	| {
			success: true;
			data: Array<string> | null;
	  }
	| {
			success: false;
			error: string;
	  };

export class ReorderTabConfigurationsDto {
	@IsArray()
	@IsNumber({}, { each: true })
	public ids!: Array<number>;

	@IsString()
	@IsIn(TAB_CONFIGURATION_LIST_KEYS)
	public listKey!: TabConfigurationListKey;
}

export type ReorderTabConfigurationsResponseDto =
	| {
			success: true;
	  }
	| {
			success: false;
			error: string;
	  };

// Re-export types from entity for convenience
export type { TabConfigurationVisibility } from '../entity/list-tab-configuration.entity';
